import { useState, useEffect } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { fmt, fmtCur } from './helpers';
import { ArrowUpRight, ArrowDownRight, Minus } from 'lucide-react';

function Delta({ val }) {
  if (!val || Math.abs(val) < 0.001) return <span className="text-slate-300">—</span>;
  if (val > 0) return <span className="flex items-center gap-0.5 text-red-600 font-mono text-xs font-semibold"><ArrowUpRight size={11} />+{fmtCur(val)}</span>;
  return <span className="flex items-center gap-0.5 text-emerald-600 font-mono text-xs font-semibold"><ArrowDownRight size={11} />{fmtCur(val)}</span>;
}

export function RevisionHistoryModal({ part, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!part?.part_id) return;
    api.get(`/parts/${part.part_id}/revision-history`)
      .then(res => setData(res.data))
      .catch(() => toast.error('Failed to load revision history'))
      .finally(() => setLoading(false));
  }, [part?.part_id]);

  if (!part) return null;

  const revisions = data?.revisions || [];

  // Compute deltas between consecutive revisions
  const withDelta = revisions.map((r, i) => {
    const prev = i < revisions.length - 1 ? revisions[i + 1] : null;
    return {
      ...r,
      sp_delta: prev ? (r.selling_price || 0) - (prev.selling_price || 0) : 0,
      rmc_delta: prev ? (r.rm_cost_net || 0) - (prev.rm_cost_net || 0) : 0,
      margin_delta: prev ? (r.contribution || 0) - (prev.contribution || 0) : 0,
    };
  });

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-slate-900">
            Revision History — <span className="font-mono text-orange-500">{part.part_number}</span>
          </DialogTitle>
          <p className="text-xs text-slate-500 mt-0.5">{part.part_name} | {part.customer_name}</p>
        </DialogHeader>

        {loading ? (
          <div className="py-8 text-center text-slate-400">Loading revisions...</div>
        ) : revisions.length <= 1 ? (
          <div className="py-8 text-center text-slate-400">No previous revisions found for this part</div>
        ) : (
          <div className="overflow-x-auto mt-2">
            <table className="w-full text-xs border-collapse" data-testid="revision-history-table">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left px-3 py-2.5 font-semibold text-slate-600 sticky left-0 bg-slate-50 z-10 min-w-[120px] border-r border-slate-200">Revision</th>
                  <th className="text-right px-3 py-2.5 font-semibold text-slate-600 min-w-[80px]">RM/Kg</th>
                  <th className="text-right px-3 py-2.5 font-semibold text-slate-600 min-w-[80px]">Net RMC</th>
                  <th className="text-right px-3 py-2.5 font-semibold text-slate-600 min-w-[70px]">Process</th>
                  <th className="text-right px-3 py-2.5 font-semibold text-slate-600 min-w-[70px]">Plating</th>
                  <th className="text-right px-3 py-2.5 font-semibold text-slate-600 min-w-[60px]">BOP</th>
                  <th className="text-right px-3 py-2.5 font-semibold text-slate-600 min-w-[70px]">Tool Maint</th>
                  <th className="text-right px-3 py-2.5 font-semibold text-slate-700 min-w-[85px]">Mfg Cost</th>
                  <th className="text-right px-3 py-2.5 font-semibold text-orange-600 min-w-[90px]">Sell Price</th>
                  <th className="text-right px-3 py-2.5 font-semibold text-slate-600 min-w-[70px]">SP Chg</th>
                  <th className="text-right px-3 py-2.5 font-semibold text-emerald-700 min-w-[80px]">Margin</th>
                  <th className="text-right px-3 py-2.5 font-semibold text-slate-600 min-w-[60px]">Margin%</th>
                </tr>
              </thead>
              <tbody>
                {withDelta.map((r, i) => {
                  const isActive = r.is_active_revision !== false;
                  return (
                    <tr key={r.part_id || i}
                      className={`border-b border-slate-100 hover:bg-slate-50 ${isActive ? 'bg-orange-50/50' : ''}`}
                      data-testid={`rev-row-${i}`}
                    >
                      <td className={`px-3 py-2.5 sticky left-0 z-10 border-r border-slate-200 ${isActive ? 'bg-orange-50/50' : 'bg-white'}`}>
                        <div className="flex items-center gap-1.5">
                          {isActive && <span className="w-1.5 h-1.5 rounded-full bg-orange-500 flex-shrink-0" />}
                          <span className={`font-semibold ${isActive ? 'text-orange-700' : 'text-slate-600'}`}>
                            {r.revision || 'N/A'}
                          </span>
                        </div>
                        {isActive && <span className="text-[9px] text-orange-500 font-bold uppercase">Active</span>}
                      </td>
                      <td className="px-3 py-2.5 text-right font-mono font-bold text-slate-800">{fmt(r.rm_price)}</td>
                      <td className="px-3 py-2.5 text-right font-mono text-slate-700">{fmtCur(r.rm_cost_net)}</td>
                      <td className="px-3 py-2.5 text-right font-mono text-slate-600">{fmtCur(r.process_cost)}</td>
                      <td className="px-3 py-2.5 text-right font-mono text-slate-600">{fmtCur(r.plating_cost)}</td>
                      <td className="px-3 py-2.5 text-right font-mono text-slate-600">{fmtCur(r.total_bop_cost)}</td>
                      <td className="px-3 py-2.5 text-right font-mono text-slate-600">{fmtCur(r.tool_maintenance_cost)}</td>
                      <td className="px-3 py-2.5 text-right font-mono font-bold text-slate-800">{fmtCur(r.total_manufacturing_cost)}</td>
                      <td className="px-3 py-2.5 text-right font-mono font-black text-orange-600">{fmtCur(r.selling_price)}</td>
                      <td className="px-3 py-2.5 text-right"><Delta val={r.sp_delta} /></td>
                      <td className="px-3 py-2.5 text-right">
                        <span className={`font-mono font-bold ${(r.contribution || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          {fmtCur(r.contribution)}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        <span className={`inline-flex px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                          (r.margin_pct || 0) >= 15 ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' :
                          (r.margin_pct || 0) >= 0 ? 'bg-red-50 text-red-700 border border-red-200' :
                          'bg-red-100 text-red-800 border border-red-300'
                        }`}>
                          {(r.margin_pct || 0).toFixed(1)}%
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
